// useWatchTracker — shared watch-depth capture for every gated replay player.
//
// Extracted from ReplayPage (registration events) when partner webinars gained
// their own gated replay, 2026-07-28. Michael's standing rule for the replay
// EMAIL mechanism — "ONE codebase, not two" (2026-06-17, see replay-email.ts) —
// applies just as much to this: the Player.js postMessage handling below is
// intricate (seek-vs-playback discrimination, beacon-on-hide, monotonic
// ratcheting) and a second copy would drift silently and under-count.
//
// The only per-entity input is the heartbeat URL. Both server endpoints accept
// the same body and both always return 200, so nothing else varies.

import { useEffect } from 'react';

/** Watch-depth capture. Listens to the Bunny player (Player.js postMessage
 *  protocol — same one the admin thumbnail picker uses) and beacons progress to
 *  the heartbeat endpoint. Tracks furthest point, engaged time, duration, and
 *  completion. Survives the tab closing via sendBeacon on hide/pagehide, which is
 *  the difference between real numbers and silent under-counting.
 *
 *  All accumulators live in a ref so updates never re-render. Progress only
 *  ratchets forward; the server upsert is monotonic too, so a lost or duplicate
 *  beat is harmless. */
export function useWatchTracker(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  opts: { heartbeatUrl: string; token: string | null; hasIframe: boolean },
) {
  const { heartbeatUrl, token, hasIframe } = opts;
  useEffect(() => {
    if (!token || !hasIframe) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const st = {
      maxPosition: 0,   // furthest point reached (seconds)
      watched: 0,       // cumulative engaged time (seconds)
      duration: 0,      // total recording length (seconds)
      completed: false,
      lastTick: null as number | null, // last currentTime while playing
      playing: false,
      dirty: false,
      sentPosition: -1, // last position we beaconed (throttle)
    };

    const num = (x: unknown): number | null =>
      typeof x === 'number' && isFinite(x) ? x : null;

    function subscribe() {
      const win = iframe?.contentWindow;
      if (!win) return;
      for (const ev of ['ready', 'timeupdate', 'play', 'pause', 'ended', 'seeked']) {
        win.postMessage(JSON.stringify({ context: 'player.js', method: 'addEventListener', value: ev, listener: 'wt_' + ev }), '*');
      }
      // Some players omit duration from timeupdate — ask explicitly.
      win.postMessage(JSON.stringify({ context: 'player.js', method: 'getDuration', listener: 'wt_duration' }), '*');
    }

    function flush(useBeacon: boolean) {
      if (!token) return;
      if (st.maxPosition <= 0 && !st.completed) return; // nothing meaningful yet
      const body = JSON.stringify({
        token,
        positionSeconds: Math.floor(st.maxPosition),
        watchedSeconds: Math.floor(st.watched),
        durationSeconds: st.duration > 0 ? Math.floor(st.duration) : undefined,
        completed: st.completed,
      });
      const url = heartbeatUrl;
      try {
        if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } else {
          void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
        }
      } catch { /* ignore */ }
      st.dirty = false;
      st.sentPosition = st.maxPosition;
    }

    function onMessage(e: MessageEvent) {
      let data: { context?: string; event?: string; value?: unknown };
      try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; }
      catch { return; }
      if (!data || typeof data !== 'object' || data.context !== 'player.js') return;
      const ev = data.event;
      const v = data.value as number | { seconds?: number; currentTime?: number; duration?: number } | undefined;

      if (ev === 'ready') { subscribe(); return; }
      if (ev === 'getDuration' || ev === 'duration') {
        const d = num(v) ?? num((v as { duration?: number })?.duration);
        if (d && d > 0) st.duration = d;
        return;
      }
      if (ev === 'play') { st.playing = true; st.lastTick = null; return; }
      if (ev === 'pause') { st.playing = false; flush(false); return; }
      if (ev === 'seeked') { st.lastTick = null; return; } // don't count the seek gap
      if (ev === 'ended') { st.completed = true; st.playing = false; flush(false); return; }
      if (ev === 'timeupdate') {
        const t = num(v) ?? num((v as { seconds?: number })?.seconds) ?? num((v as { currentTime?: number })?.currentTime);
        const d = num((v as { duration?: number })?.duration);
        if (d && d > 0) st.duration = d;
        if (t === null) return;
        if (t > st.maxPosition) st.maxPosition = t;
        // Accumulate engaged time only for small forward deltas (real playback);
        // a large jump is a seek, not watching.
        if (st.playing && st.lastTick !== null) {
          const dt = t - st.lastTick;
          if (dt > 0 && dt <= 3) st.watched += dt;
        }
        st.lastTick = t;
        if (st.duration > 0 && t >= st.duration - 5) st.completed = true;
        st.dirty = true;
        return;
      }
    }

    function onVisibility() {
      if (document.visibilityState === 'hidden' && (st.dirty || st.completed)) flush(true);
    }
    function onPageHide() { if (st.dirty || st.completed) flush(true); }

    subscribe(); // in case 'ready' already fired before we attached
    const onLoad = () => subscribe();
    iframe.addEventListener('load', onLoad);
    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    // Periodic flush while watching — throttled to genuine forward progress so a
    // paused tab doesn't beacon the same position repeatedly.
    const interval = window.setInterval(() => {
      if (st.dirty && st.maxPosition > st.sentPosition) flush(false);
    }, 20000);

    return () => {
      if (st.dirty || st.completed) flush(true);
      window.clearInterval(interval);
      iframe.removeEventListener('load', onLoad);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [iframeRef, heartbeatUrl, token, hasIframe]);
}
